import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useAuth, useUser } from "@clerk/clerk-react";
import ContentCopyRoundedIcon from "@mui/icons-material/ContentCopyRounded";
import { Alert, Box, Button, Dialog, DialogContent, IconButton, Snackbar, Stack, Typography } from "@mui/material";
import { useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import qubiqcoin from "../../assets/qubiqcoin.png";
import {
  buildInviteMessage,
  buildInviteLink,
  copyInviteText,
  consumeInvitePrompt,
  fetchInviteFriendsInfo,
  OPEN_INVITE_FRIENDS_PROMPT_EVENT,
  startInviteFriendsFlow,
} from "../../utils/inviteFriends";

const InviteFriendsPromptGate = () => {
  const { t } = useTranslation();
  const location = useLocation();
  const { isSignedIn, getToken } = useAuth();
  const { user, isLoaded } = useUser();
  const [inviteInfo, setInviteInfo] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [feedback, setFeedback] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [hasFetched, setHasFetched] = useState(false);
  const [thresholdTick, setThresholdTick] = useState(0);
  const pathnameRef = useRef(location.pathname);
  const consumedInSessionRef = useRef(false);
  const consumingRef = useRef(false);

  useEffect(() => {
    pathnameRef.current = location.pathname;
  }, [location.pathname]);

  useEffect(() => {
    if (!isSignedIn || !isLoaded || !user?.id) {
      setInviteInfo(null);
      setDialogOpen(false);
      setHasFetched(false);
      consumedInSessionRef.current = false;
      return;
    }

    let cancelled = false;

    const load = async () => {
      try {
        const nextInfo = await fetchInviteFriendsInfo(getToken);
        if (!cancelled) {
          setInviteInfo(nextInfo);
          setHasFetched(true);
        }
      } catch {
        if (!cancelled) {
          setHasFetched(true);
        }
      }
    };

    load();

    return () => {
      cancelled = true;
    };
  }, [getToken, isLoaded, isSignedIn, user?.id]);

  useEffect(() => {
    if (typeof window === "undefined") return undefined;

    const handleOpenPrompt = () => {
      if (!inviteInfo?.invitePath) return;
      setDialogOpen(true);
    };

    window.addEventListener(OPEN_INVITE_FRIENDS_PROMPT_EVENT, handleOpenPrompt);
    return () => {
      window.removeEventListener(OPEN_INVITE_FRIENDS_PROMPT_EVENT, handleOpenPrompt);
    };
  }, [inviteInfo?.invitePath]);

  const inviteLink = useMemo(
    () => buildInviteLink(inviteInfo?.invitePath),
    [inviteInfo?.invitePath]
  );
  const inviteMessage = useMemo(() => buildInviteMessage(inviteLink), [inviteLink]);

  const maybeConsumePrompt = useCallback(
    async (shouldOpen) => {
      if (consumingRef.current || consumedInSessionRef.current) return;
      consumingRef.current = true;
      consumedInSessionRef.current = true;

      try {
        await consumeInvitePrompt(getToken);
        setInviteInfo((current) =>
          current
            ? {
                ...current,
                promptConsumedAt: current.promptConsumedAt || new Date().toISOString(),
              }
            : current
        );
      } catch {}

      if (shouldOpen) {
        setDialogOpen(true);
      }

      consumingRef.current = false;
    },
    [getToken]
  );

  useEffect(() => {
    if (!isSignedIn || !isLoaded || !user?.id) return;

    const createdAtMs = user?.createdAt instanceof Date ? user.createdAt.getTime() : 0;
    if (!Number.isFinite(createdAtMs) || createdAtMs <= 0 || typeof window === "undefined") return;

    const targetTimeMs = createdAtMs + 2 * 60 * 1000;
    const armedKey = `qubiq_invite_prompt_armed:${user.id}:${createdAtMs}`;
    const resultKey = `qubiq_invite_prompt_result:${user.id}:${createdAtMs}`;
    const now = Date.now();

    const storeThresholdResult = () => {
      const isArmed = window.sessionStorage.getItem(armedKey) === "1";
      if (!isArmed) {
        window.sessionStorage.setItem(resultKey, "skip");
        setThresholdTick((current) => current + 1);
        return;
      }
      window.sessionStorage.setItem(resultKey, pathnameRef.current === "/" ? "show" : "skip");
      setThresholdTick((current) => current + 1);
    };

    if (now >= targetTimeMs) {
      if (!window.sessionStorage.getItem(resultKey)) {
        storeThresholdResult();
      }
      return;
    }

    window.sessionStorage.setItem(armedKey, "1");
    const timeoutId = window.setTimeout(storeThresholdResult, targetTimeMs - now);
    return () => {
      window.clearTimeout(timeoutId);
    };
  }, [isLoaded, isSignedIn, user?.createdAt, user?.id]);

  useEffect(() => {
    if (!isSignedIn || !isLoaded || !user?.id || !hasFetched) return;
    if (!inviteInfo || inviteInfo?.promptConsumedAt || typeof window === "undefined") return;

    const createdAtMs = user?.createdAt instanceof Date ? user.createdAt.getTime() : 0;
    if (!Number.isFinite(createdAtMs) || createdAtMs <= 0) return;

    const resultKey = `qubiq_invite_prompt_result:${user.id}:${createdAtMs}`;
    const thresholdResult = window.sessionStorage.getItem(resultKey);

    if (thresholdResult === "show") {
      void maybeConsumePrompt(true);
      return;
    }

    if (thresholdResult === "skip") {
      void maybeConsumePrompt(false);
    }
  }, [
    hasFetched,
    inviteInfo,
    isLoaded,
    isSignedIn,
    maybeConsumePrompt,
    thresholdTick,
    user?.createdAt,
    user?.id,
  ]);

  const handleInvite = async () => {
    try {
      setSubmitting(true);
      setDialogOpen(false);
      const result = await startInviteFriendsFlow({
        inviteLink,
        onCopied: () => setFeedback(t("inviteFriends.copied")),
      });
      if (result?.method === "copy") {
        setFeedback(t("inviteFriends.copied"));
      }
    } catch {
      setFeedback(t("inviteFriends.actionError"));
    } finally {
      setSubmitting(false);
    }
  };

  const handleCopyMessage = async () => {
    try {
      const copied = await copyInviteText(inviteMessage);
      setFeedback(t(copied ? "inviteFriends.copied" : "inviteFriends.actionError"));
    } catch {
      setFeedback(t("inviteFriends.actionError"));
    }
  };

  const handleCopyLink = async () => {
    try {
      const copied = await copyInviteText(inviteLink);
      setFeedback(t(copied ? "inviteFriends.copied" : "inviteFriends.actionError"));
    } catch {
      setFeedback(t("inviteFriends.actionError"));
    }
  };

  return (
    <>
      <Dialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        PaperProps={{
          sx: {
            borderRadius: "5px",
            background: "linear-gradient(180deg, #13141a 0%, #0d0e13 100%)",
            color: "#fff",
            maxWidth: 420,
            width: "calc(100% - 24px)",
            border: "1px solid rgba(255,255,255,0.08)",
          },
        }}
      >
        <DialogContent sx={{ p: { xs: 2.4, sm: 3 } }}>
          <Stack spacing={2}>
            <Typography
              sx={{
                fontFamily: "var(--display-font)",
                color: "#ffdb42",
                fontSize: "2rem",
                lineHeight: 1,
              }}
            >
              {t("inviteFriends.popupTitle")}
            </Typography>
            <Typography sx={{ color: "#fff", lineHeight: 1.5 }}>
              {t("inviteFriends.popupBodyBeforeReward")}{" "}
              <Box component="span" sx={{ display: "inline-flex", alignItems: "center", gap: 0.35 }}>
                <Box component="span">20</Box>
                <Box
                  component="img"
                  src={qubiqcoin}
                  alt="Qubiq coin"
                  sx={{ width: 15, height: 15, display: "block", transform: "translateY(-1px)" }}
                />
              </Box>{" "}
              {t("inviteFriends.popupBodyAfterReward")}
            </Typography>

            <Box
              onClick={handleCopyLink}
              role="button"
              tabIndex={0}
              onKeyDown={(event) => {
                if (event.key === "Enter" || event.key === " ") {
                  event.preventDefault();
                  void handleCopyLink();
                }
              }}
              sx={{
                position: "relative",
                borderRadius: "5px",
                border: "1px solid rgba(255,255,255,0.1)",
                backgroundColor: "rgba(255,255,255,0.04)",
                px: 1.5,
                py: 1.3,
                pr: 6,
                cursor: "pointer",
              }}
            >
              <Typography
                sx={{
                  color: "#fff",
                  fontSize: "0.78rem",
                  letterSpacing: "0.04em",
                  textTransform: "uppercase",
                  mb: 0.8,
                }}
              >
                Lien
              </Typography>
              <Typography
                sx={{
                  color: "#f5f5f7",
                  fontSize: "0.95rem",
                  lineHeight: 1.55,
                  whiteSpace: "pre-wrap",
                  wordBreak: "break-word",
                }}
              >
                {inviteLink}
              </Typography>

              <IconButton
                aria-label={t("inviteFriends.copyIconLabel")}
                onClick={(event) => {
                  event.stopPropagation();
                  void handleCopyLink();
                }}
                sx={{
                  position: "absolute",
                  right: 6,
                  bottom: 6,
                  color: "#ffdb42",
                  borderRadius: "5px",
                  backgroundColor: "rgba(255,219,66,0.08)",
                  "&:hover": {
                    backgroundColor: "rgba(255,219,66,0.16)",
                  },
                }}
              >
                <ContentCopyRoundedIcon sx={{ fontSize: 18 }} />
              </IconButton>
            </Box>

            <Box
              onClick={handleCopyMessage}
              role="button"
              tabIndex={0}
              onKeyDown={(event) => {
                if (event.key === "Enter" || event.key === " ") {
                  event.preventDefault();
                  void handleCopyMessage();
                }
              }}
              sx={{
                position: "relative",
                borderRadius: "5px",
                border: "1px solid rgba(255,255,255,0.1)",
                backgroundColor: "rgba(255,255,255,0.04)",
                px: 1.5,
                py: 1.3,
                pr: 6,
                cursor: "pointer",
              }}
            >
              <Typography
                sx={{
                  color: "#fff",
                  fontSize: "0.78rem",
                  letterSpacing: "0.04em",
                  textTransform: "uppercase",
                  mb: 0.8,
                }}
              >
                {t("inviteFriends.messageLabel")}
              </Typography>
              <Typography
                sx={{
                  color: "#f5f5f7",
                  fontSize: "0.95rem",
                  lineHeight: 1.55,
                  whiteSpace: "pre-wrap",
                  wordBreak: "break-word",
                }}
              >
                {inviteMessage}
              </Typography>

              <IconButton
                aria-label={t("inviteFriends.copyIconLabel")}
                onClick={(event) => {
                  event.stopPropagation();
                  void handleCopyMessage();
                }}
                sx={{
                  position: "absolute",
                  right: 6,
                  bottom: 6,
                  color: "#ffdb42",
                  borderRadius: "5px",
                  backgroundColor: "rgba(255,219,66,0.08)",
                  "&:hover": {
                    backgroundColor: "rgba(255,219,66,0.16)",
                  },
                }}
              >
                <ContentCopyRoundedIcon sx={{ fontSize: 18 }} />
              </IconButton>
            </Box>

            <Button
              variant="contained"
              onClick={handleInvite}
              disabled={submitting || !inviteInfo?.invitePath}
              sx={{
                alignSelf: "flex-start",
                borderRadius: "5px",
                backgroundColor: "#ffcf00",
                color: "#111217",
                textTransform: "none",
                fontWeight: 800,
                px: 2.4,
                py: 1.1,
                boxShadow: "0px 3px 0 #d38200",
                touchAction: "manipulation",
                WebkitTapHighlightColor: "transparent",
                transition: "transform 0.08s ease, box-shadow 0.08s ease, background-color 0.15s ease",
                "&:hover": {
                  backgroundColor: "#ffdb42",
                  boxShadow: "0px 3px 0 #d38200",
                },
                "&:active": {
                  transform: "translateY(3px)",
                  boxShadow: "none",
                },
                "&.Mui-disabled": {
                  backgroundColor: "rgba(255,207,0,0.35)",
                  color: "rgba(17,18,23,0.52)",
                  boxShadow: "0px 3px 0 rgba(211,130,0,0.28)",
                },
              }}
            >
              {t("inviteFriends.cta")}
            </Button>
          </Stack>
        </DialogContent>
      </Dialog>

      <Snackbar
        open={Boolean(feedback)}
        autoHideDuration={3200}
        onClose={() => setFeedback("")}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert severity="info" variant="filled" onClose={() => setFeedback("")} sx={{ backgroundColor: "#ffdb42", color: "#121216" }}>
          {feedback}
        </Alert>
      </Snackbar>
    </>
  );
};

export default InviteFriendsPromptGate;
